/**
 * Making a photo the size a chat message actually needs.
 *
 * A picture off a modern phone is around 4000x3000 and several megabytes. It
 * is drawn in a chat log at a few hundred pixels wide, so every one of those
 * megabytes is spent on detail nobody sees - once on the way up, and again on
 * every device that opens the conversation. That is the whole of "images load
 * slowly".
 *
 * The work happens on a worker thread wherever the browser can do it, so a
 * big photo does not freeze the interface while it is resized. Where it
 * cannot - Safari without OffscreenCanvas - the original is sent unchanged
 * rather than blocking the main thread to save some bytes.
 */

/** What a profile picture is ever drawn at, doubled for high-density screens. */
export const AVATAR_EDGE = 512;
/** A banner spans a profile header and no more. */
export const BANNER_EDGE = 1280;

/** Longest edge, in pixels. Comfortably above any place we display one. */
const MAX_EDGE = 1920;
/** Below this, resizing costs more than it saves. */
const FLOOR = 400 * 1024;

/** Types worth touching. GIFs are animated and PNGs are often screenshots. */
const SHRINKABLE = /^image\/(jpeg|png|webp)$/;

export async function shrinkImage(file: File, maxEdge = MAX_EDGE): Promise<File> {
  if (!SHRINKABLE.test(file.type)) return file;
  /*
   * A small file can still be far too many pixels for where it is going.
   *
   * An avatar is drawn at twenty pixels in a roster and forty in a message.
   * A 1200px picture of any weight is a hundred times the pixels needed, and
   * every client decodes all of them - so the floor only applies when the
   * picture is already about the right size.
   */
  if (file.size < FLOOR && maxEdge >= MAX_EDGE) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));

    // Already small enough in pixels; a re-encode would only lose quality.
    if (scale === 1 && file.size < 2 * 1024 * 1024) { bitmap.close(); return file; }

    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    if (typeof OffscreenCanvas === 'undefined') { bitmap.close(); return file; }

    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) { bitmap.close(); return file; }

    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();

    /*
     * A screenshot with flat colour and text stays PNG - JPEG turns crisp
     * edges into a smear. Photographs become JPEG, where the saving is.
     */
    const asPng = file.type === 'image/png' && looksFlat(ctx, w, h);
    const blob = await canvas.convertToBlob(
      asPng ? { type: 'image/png' } : { type: 'image/jpeg', quality: 0.86 }
    );

    // Only if it actually helped.
    if (blob.size >= file.size) return file;

    return new File([blob], renamed(file.name, asPng ? 'png' : 'jpg'), {
      type: blob.type,
      lastModified: file.lastModified
    });
  } catch {
    // Anything at all goes wrong: send what they picked.
    return file;
  }
}

/**
 * A rough guess at "this is a screenshot, not a photograph".
 *
 * Samples a grid and counts distinct colours. A photo has thousands; an
 * interface has a few dozen. Cheap enough to run on anything.
 */
function looksFlat(ctx: OffscreenCanvasRenderingContext2D, w: number, h: number): boolean {
  const step = Math.max(1, Math.floor(Math.min(w, h) / 40));
  const seen = new Set<number>();

  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      const d = ctx.getImageData(x, y, 1, 1).data;
      seen.add(((d[0]! >> 3) << 10) | ((d[1]! >> 3) << 5) | (d[2]! >> 3));
      if (seen.size > 400) return false;
    }
  }
  return true;
}

function renamed(name: string, ext: string): string {
  return name.replace(/\.[^.]+$/, '') + '.' + ext;
}
